/**
 * Markazdan qochma nasos xarakteristikalari.
 *
 * MANBA: Kasatkin 3-bob (nasoslar: xarakteristika, ish nuqtasi, kavitatsiya, NPSH/so'rish balandligi);
 *        Pavlov-Romankov-Noskov, 1-bob (nasos hisobi);
 *        Perry's 8th ed., Sec. 10 (Pumps: affinity laws, NPSH);
 *        Karassik, Pump Handbook (η(Q) parabola yaqinlashishi).
 *
 * TENGLAMALAR (r = n/n0 — aylanishlar chastotasi nisbati)
 *   Nominal:      H0(Q) = H0 − a·Q − b·Q²
 *   O'xshashlik (affinity): Q ∝ n, H ∝ n², P ∝ n³  →  H(Q, r) = r²·H0(Q/r) = r²·H0 − a·r·Q − b·Q²
 *   Samaradorlik: η(Q, r) = η_max · [1 − ((Q − r·Q_bep)/(r·Q_bep))²],  0 dan kichik bo'lmaydi
 *   NPSH_r(Q, r) = r² · Σ c_i · (Q/r)^i
 *   NPSH_a = (P_s − P_vap(T))/(ρg) + z_s − h_f,s − h_m,s
 *   3 nuqtadan fit: [1, −Q_i, −Q_i²]·[H0, a, b]ᵀ = H_i  (linsolve)
 *
 * ASSUMPTION:
 *   1. Xarakteristika kvadratik parabola (a, b ≥ 0 — monoton pasayuvchi; "g'ur" (hump) yo'q).
 *   2. η(Q) simmetrik parabola, η(0) = η(2·Q_bep) = 0; η → 0 da val quvvati cheksiz bo'lmasligi
 *      uchun η ≥ ETA_FLOOR = 0.02 qabul qilingan (real nasos yopiq zulfinda ~30–50% nominal quvvat oladi;
 *      bu model uni bermaydi).
 *   3. Affinity qonunlari η ni o'zgartirmaydi (Re-effekt e'tiborsiz).
 *   4. Teskari oqim (nasos orqali) yo'q — teskari klapan (check valve) bor deb hisoblanadi.
 */
import { linsolve } from '../../solvers/linsolve';
import type { PumpCurve, PumpParams, PumpPoint } from './types';

export const ETA_FLOOR = 0.02;

/** 3 nuqtadan H = H0 − a·Q − b·Q² fit */
export function fitPumpCurve(points: readonly [PumpPoint, PumpPoint, PumpPoint]): PumpCurve {
  const A = points.map((p) => [1, -p.Q, -p.Q * p.Q]);
  const x = linsolve(
    A,
    points.map((p) => p.H),
  );
  if (!x) throw new Error('pump-pipeline: curvePoints are degenerate (equal Q values?)');
  return { H0: x[0] as number, a: x[1] as number, b: x[2] as number };
}

export function resolveCurve(p: PumpParams): PumpCurve {
  const c = p.curve ?? (p.curvePoints ? fitPumpCurve(p.curvePoints) : undefined);
  if (!c) throw new Error('pump-pipeline: pump.curve or pump.curvePoints required');
  if (!(c.H0 > 0)) throw new Error('pump-pipeline: pump curve H0 must be > 0');
  if (!(c.a >= 0 && c.b >= 0))
    throw new Error('pump-pipeline: pump curve must be monotonically decreasing (a, b ≥ 0)');
  return c;
}

/** Nasos napori H(Q, r), m. r = 0 → 0 (nasos o'chiq). */
export function pumpHead(c: PumpCurve, Q: number, r: number): number {
  if (!(r > 0)) return 0;
  return r * r * c.H0 - c.a * r * Q - c.b * Q * Q;
}

/** dH/dQ */
export function pumpHeadSlope(c: PumpCurve, Q: number, r: number): number {
  if (!(r > 0)) return 0;
  return -c.a * r - 2 * c.b * Q;
}

/** Nasos xarakteristikasi nolga tushadigan sarf (r da), m³/s */
export function pumpMaxFlow(c: PumpCurve, r: number): number {
  if (!(r > 0)) return 0;
  const H = r * r * c.H0;
  const A = c.a * r;
  if (c.b > 0) return (-A + Math.sqrt(A * A + 4 * c.b * H)) / (2 * c.b);
  if (A > 0) return H / A;
  return Infinity;
}

export function pumpEfficiency(p: PumpParams, Q: number, r: number): number {
  if (!(r > 0)) return ETA_FLOOR;
  const x = (Q - r * p.Qbep) / (r * p.Qbep);
  return Math.max(ETA_FLOOR, p.etaMax * (1 - x * x));
}

export function npshRequired(p: PumpParams, Q: number, r: number): number {
  if (!(r > 0)) return 0;
  const q = Q / r;
  let s = 0;
  let qn = 1;
  for (const c of p.npshRequired) {
    s += c * qn;
    qn *= q;
  }
  return r * r * s;
}

export function validatePump(p: PumpParams): void {
  resolveCurve(p);
  if (!(p.etaMax > 0 && p.etaMax <= 1))
    throw new Error('pump-pipeline: pump.etaMax must be in (0, 1]');
  if (!(p.Qbep > 0)) throw new Error('pump-pipeline: pump.Qbep must be > 0');
  if (p.npshRequired.length === 0)
    throw new Error('pump-pipeline: pump.npshRequired needs ≥ 1 coefficient');
}
